import type { OfflineRequest } from "./offline-outbox";
import { queueGroupRevision } from "./offline-recovery-model";

export const abandonOfflineGroupConfirmation = "确认放弃整组";
export const restoreOfflineGroupConfirmation = "恢复整组";

export const offlineGroupAbandonmentReasons = [
  "duplicate_request",
  "operation_canceled",
  "administrator_verified",
  "other"
] as const;
export type OfflineGroupAbandonmentReason = (typeof offlineGroupAbandonmentReasons)[number];

export type AbandonOfflineGroupCommand = {
  groupId: string;
  revision: string;
  base: string;
  reason: OfflineGroupAbandonmentReason;
  note: string;
  confirmation: string;
};

export type RestoreAbandonedOfflineGroupCommand = {
  abandonmentId: string;
  revision: string;
  base: string;
  confirmation: string;
};

export type AbandonedOfflineRequest = OfflineRequest & {
  archive: {
    reason: "group_abandoned";
    abandonmentId: string;
    abandonmentReason: OfflineGroupAbandonmentReason;
    abandonmentNote: string;
    archivedAt: string;
    archivedAtBase: string;
    originalRevision: string;
    requestIds: string[];
    dedupeKeys: string[];
    restoredAt?: string;
    restoredAtBase?: string;
  };
};

export type OfflineGroupAbandonmentPlan = {
  archived: AbandonedOfflineRequest[];
};

export type OfflineGroupRestorePlan = {
  restored: OfflineRequest[];
  updatedArchives: AbandonedOfflineRequest[];
};

function normalizedBase(base: string) {
  return base.trim() || globalThis.location?.origin || "";
}

function activeGroup(items: OfflineRequest[], scopeKey: string, groupId: string) {
  return items.filter(item => item.scopeKey === scopeKey && item.groupId === groupId);
}

function validReason(reason: string): reason is OfflineGroupAbandonmentReason {
  return offlineGroupAbandonmentReasons.includes(reason as OfflineGroupAbandonmentReason);
}

function sortedStrings(values: string[]) {
  return [...values].sort((a, b) => a.localeCompare(b));
}

function sameStringSet(left: string[], right: string[]) {
  return left.length === right.length && JSON.stringify(sortedStrings(left)) === JSON.stringify(sortedStrings(right));
}

function operationIdentity(item: OfflineRequest) {
  return item.operationKey ? JSON.stringify([item.serverBase, item.operationKey]) : "";
}

/** 返回不能放弃整组的原因；空字符串表示可以进入二次确认。 */
export function offlineGroupAbandonmentBlockReason(items: OfflineRequest[]) {
  if (!items.length) return "该组已变化或已补传，请刷新队列";
  if (items.some(item => !item.recovery?.paused)) return "必须先暂停整个分组，确认没有请求正在发送后才能放弃";
  return "";
}

export function abandonedOfflineGroupRevision(items: AbandonedOfflineRequest[]) {
  return JSON.stringify(
    [...items]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map(item => [
        item.id,
        item.updatedAt,
        item.status,
        item.attempts,
        item.serverBase,
        item.archive.abandonmentId,
        item.archive.archivedAt,
        item.archive.restoredAt || ""
      ])
  );
}

/**
 * 生成整组放弃归档计划。调用方必须在同一 IndexedDB 事务内先写归档再移除活动记录。
 * 原请求体、照片、请求 ID、幂等键、派工轮次及失败信息全部保留在归档中。
 */
export function planOfflineGroupAbandonment(
  scopeKey: string,
  items: OfflineRequest[],
  command: AbandonOfflineGroupCommand,
  createId: () => string,
  now = new Date().toISOString()
): OfflineGroupAbandonmentPlan {
  const group = activeGroup(items, scopeKey, command.groupId);
  if (!group.length || queueGroupRevision(group) !== command.revision) throw new Error("队列已变化，请重新打开处理窗口后核对");
  const blockReason = offlineGroupAbandonmentBlockReason(group);
  if (blockReason) throw new Error(blockReason);
  if (command.confirmation.trim() !== abandonOfflineGroupConfirmation) throw new Error(`请输入“${abandonOfflineGroupConfirmation}”完成二次确认`);
  if (!validReason(command.reason)) throw new Error("请选择有效的整组放弃原因");
  const note = command.note.trim();
  if (note.length < 8) throw new Error("请填写至少 8 个字符的具体原因，便于后续审计和恢复核对");
  if (note.length > 4000) throw new Error("整组放弃说明不能超过 4000 个字符");
  const abandonmentId = createId().trim();
  if (!abandonmentId) throw new Error("无法生成整组放弃审计标识");
  const requestIds = group.map(item => item.id);
  const dedupeKeys = group.map(item => item.dedupeKey);
  const originalRevision = queueGroupRevision(group);
  const archivedAtBase = normalizedBase(command.base);
  const archived = group.map<AbandonedOfflineRequest>(item => ({
    ...item,
    archive: {
      reason: "group_abandoned",
      abandonmentId,
      abandonmentReason: command.reason,
      abandonmentNote: note,
      archivedAt: now,
      archivedAtBase,
      originalRevision,
      requestIds,
      dedupeKeys
    }
  }));
  return { archived };
}

/**
 * 误操作恢复只把原记录恢复为“暂停、仅前台处理”状态，不自动补传。
 * 永久保留放弃归档，并写入恢复时间形成审计闭环。
 */
export function planAbandonedOfflineGroupRestore(
  scopeKey: string,
  activeItems: OfflineRequest[],
  archivedItems: AbandonedOfflineRequest[],
  command: RestoreAbandonedOfflineGroupCommand,
  now = new Date().toISOString()
): OfflineGroupRestorePlan {
  const archived = archivedItems.filter(item => item.scopeKey === scopeKey && item.archive.abandonmentId === command.abandonmentId);
  if (!archived.length || abandonedOfflineGroupRevision(archived) !== command.revision) throw new Error("放弃归档已变化，请刷新后重新核对");
  if (archived.some(item => item.archive.reason !== "group_abandoned" || item.archive.restoredAt)) throw new Error("该放弃归档已恢复或不再可恢复");
  if (new Set(archived.map(item => item.groupId)).size !== 1) throw new Error("放弃归档分组关系异常，不能自动恢复");
  const [first] = archived;
  const expectedRequestIds = first.archive.requestIds;
  const expectedDedupeKeys = first.archive.dedupeKeys;
  if (!expectedRequestIds.length || !expectedDedupeKeys.length || new Set(expectedRequestIds).size !== expectedRequestIds.length || new Set(expectedDedupeKeys).size !== expectedDedupeKeys.length || !sameStringSet(expectedRequestIds, archived.map(item => item.id)) || !sameStringSet(expectedDedupeKeys, archived.map(item => item.dedupeKey))) {
    throw new Error("放弃归档不完整或组内关系已损坏，不能自动恢复");
  }
  if (archived.some(item => item.archive.originalRevision !== first.archive.originalRevision || item.archive.archivedAt !== first.archive.archivedAt || item.archive.archivedAtBase !== first.archive.archivedAtBase || item.archive.abandonmentReason !== first.archive.abandonmentReason || item.archive.abandonmentNote !== first.archive.abandonmentNote || !sameStringSet(item.archive.requestIds, expectedRequestIds) || !sameStringSet(item.archive.dedupeKeys, expectedDedupeKeys))) {
    throw new Error("放弃归档审计关系不一致，不能自动恢复");
  }
  if (command.confirmation.trim() !== restoreOfflineGroupConfirmation) throw new Error(`请输入“${restoreOfflineGroupConfirmation}”确认恢复`);
  const activeForScope = activeItems.filter(item => item.scopeKey === scopeKey);
  const activeIds = new Set(activeForScope.map(item => item.id));
  const activeKeys = new Set(activeForScope.map(item => item.dedupeKey));
  const activeOperations = new Set(activeForScope.map(operationIdentity).filter(Boolean));
  if (archived.some(item => activeIds.has(item.id))) throw new Error("活动队列中已存在同一请求记录，不能重复恢复");
  if (archived.some(item => activeKeys.has(item.dedupeKey))) throw new Error("活动队列中已存在相同幂等标识，不能重复恢复");
  if (archived.some(item => operationIdentity(item) && activeOperations.has(operationIdentity(item)))) throw new Error("活动队列中已存在相同业务标识（同一服务器），不能恢复旧组以免重复提交");
  const order = new Map(expectedRequestIds.map((id, index) => [id, index]));
  const orderedArchived = [...archived].sort((a, b) => (order.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.id) ?? Number.MAX_SAFE_INTEGER));
  const restored = orderedArchived.map<OfflineRequest>(item => {
    const { archive: _archive, ...request } = item;
    return {
      ...request,
      recovery: {
        paused: true,
        changedAt: now,
        ...(item.recovery?.serverConfirmedAt ? { serverConfirmedAt: item.recovery.serverConfirmedAt } : {})
      },
      backgroundEligible: false,
      updatedAt: now
    };
  });
  const updatedArchives = orderedArchived.map<AbandonedOfflineRequest>(item => ({
    ...item,
    archive: {
      ...item.archive,
      restoredAt: now,
      restoredAtBase: normalizedBase(command.base)
    }
  }));
  return { restored, updatedArchives };
}
